import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Badge, Button, Card, Select, SelectItem, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import { fetchSales, fetchSalesSummary } from '../api/drilldown';
import DateRangePresets from '../components/DateRangePresets';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import Tour, { hasTourBeenSeen, markTourSeen } from '../components/Tour';
import { TOURS } from '../lib/tours';
import type { SalesQuery, SalesSummaryQuery } from '../api/drilldown';
import { currentUser } from '../api/auth';
import { localYMD } from '../lib/format';
import { useStoreFilter } from '../lib/storeFilter';
import { usePagination } from '../lib/usePagination';
import Pagination from '../components/Pagination';
import { t } from '../lib/i18n';
import type { SalesSummary, SalesTicket, User } from '../api/types';
import Header from '../components/Header';
import ExportButtons from '../components/ExportButtons';
import type { ExportColumn } from '../lib/exportTable';
import LinePartsDrilldownModal from '../components/LinePartsDrilldownModal';
import SalesSummaryPanel from '../components/SalesSummaryPanel';
import DrillCell from '../components/DrillCell';
import { useDrilldown } from '../components/DrilldownProvider';

/** YYYY-MM-DD for an n-days-ago Date in the user's local timezone. */
function dateNDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localYMD(d);
}

/** handle-sales stops emitting after this many tickets; the summary has no cap. */
const RAW_ROW_CAP = 5000;

const fmtMxn = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 2,
});

// Export column spec — same labels as the table; CSV keeps raw numbers, PDF
// gets the on-screen money formatting. Declared once at module level (only
// depends on i18n + the formatter).
const SALES_EXPORT: ExportColumn<SalesTicket>[] = [
  { label: t.sales.cols.date,    value: (r) => r.idate },
  { label: t.sales.cols.ticket,  value: (r) => r.tnum, numeric: true },
  { label: t.sales.cols.account, value: (r) => (r.anum > 0 ? r.anum : ''), numeric: true },
  { label: t.sales.cols.store,   value: (r) => r.sid, numeric: true },
  { label: t.sales.cols.slid,    value: (r) => r.slid },
  { label: t.sales.cols.subt,    value: (r) => r.subt_mxn,  numeric: true, pdf: (r) => fmtMxn.format(r.subt_mxn) },
  { label: t.sales.cols.tax,     value: (r) => r.tax_mxn,   numeric: true, pdf: (r) => fmtMxn.format(r.tax_mxn) },
  { label: t.sales.cols.total,   value: (r) => r.total_mxn, numeric: true, pdf: (r) => fmtMxn.format(r.total_mxn) },
  {
    label: t.sales.cols.type,
    value: (r) => {
      const credit = r.type === 0 || r.type === 1;
      const debit = r.type === 1 || r.type === 3;
      return (credit ? t.sales.badge.credit : t.sales.badge.cash) + (debit ? ` (${t.sales.badge.debit})` : '');
    },
  },
  {
    label: t.sales.cols.balance,
    value: (r) => (r.balance_mxn == null ? '' : r.balance_mxn),
    numeric: true,
    pdf: (r) => (r.balance_mxn == null ? '—' : r.balance_mxn > 0 ? fmtMxn.format(r.balance_mxn) : t.sales.badge.paid),
  },
];

export default function Sales() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  return user ? <SalesInner user={user} /> : <Navigate to="/login" replace />;
}

export function SalesInner({ user, embedded = false }: { user: User; embedded?: boolean }) {
  // Default to today: on large stores a wide default (e.g. 7/30d) pulls
  // thousands of tickets with line items — seconds of daemon scan. "Hoy"
  // loads fast; the 7d/30d presets remain one click away.
  const [from, setFrom] = useState(dateNDaysAgo(0));
  const [to, setTo] = useState(dateNDaysAgo(0));
  const [typeFilter, setTypeFilter] = useState<'all' | 'cash' | 'credit'>('all');
  const [paidFilter, setPaidFilter] = useState<'all' | 'unpaid' | 'paid'>('all');
  const [data, setData] = useState<SalesTicket[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Server-side aggregate for the same window. Fetched alongside the raw
  // list; on wide windows it lands long before the detail (one flat scan vs
  // a per-ticket emit + artrx lookup), so KPIs + charts paint first.
  const [summary, setSummary] = useState<SalesSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  // Ticket + account detail come from the app-wide drill-down stack (JCD-59),
  // so a part clicked inside the ticket modal nests with a back arrow instead
  // of navigating away from these filters.
  const { open: openDrill } = useDrilldown();
  // "Por línea" chart click → top parts for that line (needs the raw rows).
  const [drilledLine, setDrilledLine] = useState<string | null>(null);
  const [tourOpen, setTourOpen] = useState(false);
  useEffect(() => {
    if (!data) return;
    if (!hasTourBeenSeen(TOURS.sales.key)) {
      const id = setTimeout(() => setTourOpen(true), 600);
      return () => clearTimeout(id);
    }
  }, [data]);
  useEffect(() => {
    const onOpen = () => setTourOpen(true);
    window.addEventListener('pacebi:open-tour', onOpen);
    return () => window.removeEventListener('pacebi:open-tour', onOpen);
  }, []);

  // Shared header store (lib/storeFilter). Staff stay locked to their own
  // store; the daemon enforces that server-side too.
  const [storeFilter] = useStoreFilter(user);

  // The summary has no paid/pending pass (that per-ticket artrx lookup is the
  // slow part it skips), so with a paid filter active its numbers would not
  // match the table — show the raw-derived strip alone in that case.
  const summaryMode = paidFilter === 'all';

  const load = () => {
    let alive = true;
    setLoading(true);
    setError(null);
    const q: SalesQuery = { from, to, type_filter: typeFilter, paid_filter: paidFilter };
    if (storeFilter !== null) q.sid = storeFilter;
    fetchSales(q)
      .then((rows) => { if (alive) setData(rows); })
      .catch((e: Error) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });

    if (summaryMode) {
      setSummary(null);
      setSummaryError(null);
      setSummaryLoading(true);
      const sq: SalesSummaryQuery = { from, to, type_filter: typeFilter };
      if (storeFilter !== null) sq.sid = storeFilter;
      fetchSalesSummary(sq)
        .then((s) => { if (alive) setSummary(s); })
        .catch((e: Error) => { if (alive) setSummaryError(e.message); })
        .finally(() => { if (alive) setSummaryLoading(false); });
    } else {
      setSummary(null);
      setSummaryError(null);
      setSummaryLoading(false);
    }
    return () => { alive = false; };
  };
  useEffect(() => {
    const cancel = load();
    return cancel;
  }, [from, to, typeFilter, paidFilter, storeFilter]);

  const totals = useMemo(() => {
    if (!data) return { count: 0, total: 0, unpaid: 0 };
    let total = 0, unpaid = 0;
    for (const r of data) {
      total += r.total_mxn;
      if (r.balance_mxn !== null && r.balance_mxn > 0) unpaid += r.balance_mxn;
    }
    return { count: data.length, total, unpaid };
  }, [data]);

  const pg = usePagination(data);
  const rawCapped = data !== null && data.length >= RAW_ROW_CAP;

  const content = (
    <>
        <Title>{t.sales.title}</Title>
        <Text className="text-tremor-content text-sm">{t.sales.subtitle}</Text>

        <Card className="space-y-4">
          {/* Date presets above + manual inputs below — most operators want
              "last 7 days" or "this month" in one click; the manual inputs
              stay for ad-hoc ranges. */}
          <div data-tour="sales-presets">
            <DateRangePresets
              from={from}
              to={to}
              onChange={({ from: f, to: t2 }) => { setFrom(f); setTo(t2); }}
            />
          </div>
          <div className="flex flex-wrap gap-4 items-end" data-tour="sales-filters">
            <label className="text-sm">
              <div className="text-tremor-content text-xs mb-1">{t.sales.typeFilter}</div>
              <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as typeof typeFilter)} className="w-40">
                <SelectItem value="all">{t.sales.typeAll}</SelectItem>
                <SelectItem value="cash">{t.sales.typeCash}</SelectItem>
                <SelectItem value="credit">{t.sales.typeCredit}</SelectItem>
              </Select>
            </label>
            <label className="text-sm">
              <div className="text-tremor-content text-xs mb-1">{t.sales.paidFilter}</div>
              <Select value={paidFilter} onValueChange={(v) => setPaidFilter(v as typeof paidFilter)} className="w-40">
                <SelectItem value="all">{t.sales.paidAll}</SelectItem>
                <SelectItem value="unpaid">{t.sales.paidUnpaid}</SelectItem>
                <SelectItem value="paid">{t.sales.paidPaid}</SelectItem>
              </Select>
            </label>
            <Button
              size="xs"
              variant="light"
              onClick={() => {
                setFrom(dateNDaysAgo(7));
                setTo(dateNDaysAgo(0));
                setTypeFilter('all');
                setPaidFilter('all');
              }}
            >
              Limpiar filtros
            </Button>
            <Button size="xs" variant="secondary" loading={loading} onClick={load}>
              ↻ Actualizar
            </Button>
            <ExportButtons
              title={t.sales.title}
              filename={`Ventas ${from} ${to}`}
              meta={[
                `${from} – ${to}`,
                ...(storeFilter !== null ? [`${t.filter.store} ${storeFilter}`] : []),
                ...(typeFilter !== 'all' ? [`${t.sales.typeFilter}: ${typeFilter === 'cash' ? t.sales.typeCash : t.sales.typeCredit}`] : []),
                ...(paidFilter !== 'all' ? [`${t.sales.paidFilter}: ${paidFilter === 'paid' ? t.sales.paidPaid : t.sales.paidUnpaid}`] : []),
              ]}
              printedBy={user.username}
              columns={SALES_EXPORT}
              rows={data}
              orientation="landscape"
            />
          </div>
          <div className="ml-auto flex flex-col items-end gap-1 text-sm">
            {summaryMode ? (
              data && (
                <>
                  <Text>{t.sales.summary.detailLoaded.replace('{n}', String(totals.count))}</Text>
                  {rawCapped && (
                    <Text className="text-amber-700 dark:text-amber-400">
                      {t.sales.summary.detailCapped.replace('{n}', String(RAW_ROW_CAP))}
                    </Text>
                  )}
                </>
              )
            ) : (
              <>
                <Text>
                  {totals.count} tickets · Total {fmtMxn.format(totals.total)}
                </Text>
                {totals.unpaid > 0 && (
                  <Text className="text-red-600 dark:text-red-400">{t.sales.unpaidLabel}: {fmtMxn.format(totals.unpaid)}</Text>
                )}
                <Text className="text-xs text-tremor-content">{t.sales.summary.paidFilterNote}</Text>
              </>
            )}
          </div>
        </Card>

        {summaryMode && (
          <SalesSummaryPanel
            summary={summary}
            loading={summaryLoading}
            error={summaryError}
            onRetry={load}
            pendingMxn={data ? totals.unpaid : null}
            onLineClick={data && data.length > 0 ? setDrilledLine : undefined}
          />
        )}

        {error && <ErrorRetry message={error} onRetry={load} />}
        {loading && !data && (
          <>
            {summaryMode && (
              <Text className="text-sm text-tremor-content animate-pulse">{t.sales.summary.tableLoading}</Text>
            )}
            <TableSkeleton rows={8} cols={7} />
          </>
        )}
        {data && data.length === 0 && !loading && (
          <EmptyState message={t.sales.empty} />
        )}
        {data && data.length > 0 && (
          <Card className="p-0 overflow-hidden">
            <Text className="px-4 pt-3 text-xs text-tremor-content">
              {t.sales.tableHint}
            </Text>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.sales.cols.date}</TableHeaderCell>
                  <TableHeaderCell>{t.sales.cols.ticket}</TableHeaderCell>
                  <TableHeaderCell>{t.sales.cols.account}</TableHeaderCell>
                  <TableHeaderCell>{t.sales.cols.store}</TableHeaderCell>
                  <TableHeaderCell>{t.sales.cols.slid}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.sales.cols.subt}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.sales.cols.tax}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.sales.cols.total}</TableHeaderCell>
                  <TableHeaderCell>{t.sales.cols.type}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.sales.cols.balance}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pg.pageRows.map((row) => {
                  const isCredit = row.type === 0 || row.type === 1;
                  const isDebit  = row.type === 1 || row.type === 3;
                  // Inner clicks shouldn't also trigger the row click — the
                  // account drill has to win over the ticket drill (JCD-59).
                  const accountCell = row.anum > 0 ? (
                    <span onClick={(e) => e.stopPropagation()}>
                      <DrillCell target={{ kind: 'account', anum: row.anum }}>
                        {row.anum}
                      </DrillCell>
                    </span>
                  ) : (
                    '—'
                  );
                  return (
                    <TableRow
                      key={`${row.sid}-${row.tnum}`}
                      className="cursor-pointer hover:bg-tremor-background-muted"
                      onClick={() => openDrill({ kind: 'tkt', sid: row.sid, tnum: row.tnum })}
                    >
                      <TableCell>{row.idate}</TableCell>
                      <TableCell>{row.tnum}</TableCell>
                      <TableCell>{accountCell}</TableCell>
                      <TableCell>{row.sid}</TableCell>
                      <TableCell>{row.slid || '—'}</TableCell>
                      <TableCell className="text-right">{fmtMxn.format(row.subt_mxn)}</TableCell>
                      <TableCell className="text-right">{fmtMxn.format(row.tax_mxn)}</TableCell>
                      <TableCell className="text-right">{fmtMxn.format(row.total_mxn)}</TableCell>
                      <TableCell>
                        <Badge color={isCredit ? 'amber' : 'emerald'}>
                          {isCredit ? t.sales.badge.credit : t.sales.badge.cash}
                          {isDebit ? ` (${t.sales.badge.debit})` : ''}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {row.balance_mxn === null ? (
                          <span className="text-tremor-content">—</span>
                        ) : row.balance_mxn > 0 ? (
                          <Badge color="red">{fmtMxn.format(row.balance_mxn)}</Badge>
                        ) : (
                          <Badge color="green">{t.sales.badge.paid}</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Pagination {...pg} className="px-4" />
          </Card>
        )}
      {drilledLine && data && (
        <LinePartsDrilldownModal
          tickets={data}
          line={drilledLine}
          onClose={() => setDrilledLine(null)}
        />
      )}
      {tourOpen && (
        <Tour
          tourKey={TOURS.sales.key}
          steps={TOURS.sales.steps}
          onClose={() => { setTourOpen(false); markTourSeen(TOURS.sales.key); }}
        />
      )}
    </>
  );

  if (embedded) return <div className="space-y-4">{content}</div>;
  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} storeFilter />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        {content}
      </main>
    </div>
  );
}
